// Dev & diagnostics page — hosts every M0/M1 smoke-test surface the old
// HomePage used to render. Keeps these around so we can poke at Worker
// health, RFQ extract, watchers, etc. without cluttering the real dashboard.

import { useEffect, useState, type ReactNode } from 'react';
import { RfqExtractTest } from '@/components/RfqExtractTest';
import { OutlookDraftTest } from '@/components/OutlookDraftTest';
import { RemindersPanel } from '@/components/RemindersPanel';
import { WatcherAdmin } from '@/components/WatcherAdmin';
import { DevToolsCard } from '@/components/DevToolsCard';
import { DevDataPanel } from '@/components/DevDataPanel';
import { config } from '@/shared/config';
import { firebaseConfigured } from '@/shared/firebase';
import { msalConfigured } from '@/shared/msalConfig';

type WorkerHealth = 'unknown' | 'ok' | 'error';

// Section header rendered above every group on the page. The component
// itself (OutlookDraftTest, etc.) brings its own card chrome — this is just
// a label + spacer so the page reads top-to-bottom.
function SectionLabel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-text-mute mb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

// Inline cards (Worker health + Setup checklist) — these don't have a
// component of their own, so DevPage owns the card chrome.
function CardSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-text-mute mb-2">
        {title}
      </h2>
      <div className="bg-paper border border-line rounded p-4">{children}</div>
    </section>
  );
}

function WorkerHealthBody({
  status,
  details,
}: {
  status: WorkerHealth;
  details: Record<string, unknown> | null;
}) {
  if (status === 'unknown') return <p className="text-sm text-text-mute">Checking…</p>;
  if (status === 'error') {
    return (
      <div className="text-sm">
        <p className="text-orange font-semibold mb-2">
          ⚠ Worker unreachable at {config.workerUrl}
        </p>
        <p className="text-text-mute">
          Run <code className="bg-bg px-1 rounded">npm run worker:dev</code> in a separate
          terminal, then refresh.
        </p>
      </div>
    );
  }
  if (status === 'ok' && details) {
    return (
      <ul className="text-sm space-y-1 font-mono">
        <li>
          <span className="text-text-mute">version:</span>{' '}
          <span className="text-navy">{String(details.worker_version)}</span>
        </li>
        <li>
          <span className="text-text-mute">model:</span>{' '}
          <span className="text-navy">{String(details.model)}</span>
        </li>
        <li>
          <span className="text-text-mute">anthropic_key:</span>{' '}
          <span className={details.has_anthropic_key ? 'text-green' : 'text-orange'}>
            {details.has_anthropic_key ? '✓ configured' : '✗ not set'}
          </span>
        </li>
      </ul>
    );
  }
  return null;
}

function SetupChecklistBody({
  workerHealth,
  healthDetails,
}: {
  workerHealth: WorkerHealth;
  healthDetails: Record<string, unknown> | null;
}) {
  return (
    <>
      <ul className="text-sm space-y-2">
        <li>✅ Vite + React + TS + Tailwind scaffold</li>
        <li>✅ Worker scaffold with /api/health + /api/brain/chat</li>
        <li>✅ Brain SDK (chat, extract, compose, recall, remember, remind, flag, watchers)</li>
        <li>{workerHealth === 'ok' ? '✅' : '⏳'} Worker reachable</li>
        <li>{healthDetails?.has_anthropic_key ? '✅' : '⏳'} ANTHROPIC_API_KEY set (Worker secret)</li>
        <li>
          {firebaseConfigured ? '✅' : '⏳'} Firebase project created → config in{' '}
          <code className="bg-bg px-1 rounded">src/shared/firebase.ts</code>
        </li>
        <li>
          {msalConfigured ? '✅' : '⏳'} MS Graph configured → set{' '}
          <code className="bg-bg px-1 rounded">VITE_MSAL_CLIENT_ID</code> +{' '}
          <code className="bg-bg px-1 rounded">VITE_MSAL_TENANT_ID</code> in{' '}
          <code className="bg-bg px-1 rounded">.env.local</code>
        </li>
        <li>⏳ Worker deployed to Cloudflare → URL in <code className="bg-bg px-1 rounded">.env.local</code></li>
        <li>⏳ GitHub repo + Pages configured</li>
      </ul>
      <p className="text-xs text-text-mute mt-3">
        See <code>README.md</code> "First-real-run checklist" for setup steps.
      </p>
    </>
  );
}

export function DevPage() {
  const [workerHealth, setWorkerHealth] = useState<WorkerHealth>('unknown');
  const [healthDetails, setHealthDetails] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    fetch(`${config.workerUrl}/api/health`)
      .then((r) => r.json())
      .then((data) => {
        setWorkerHealth('ok');
        setHealthDetails(data);
      })
      .catch(() => setWorkerHealth('error'));
  }, []);

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-navy mb-2">Dev & diagnostics</h1>
      <p className="text-text-mute mb-6">
        M0/M1 smoke-test surfaces + tools. Production modules live in the main nav.
      </p>

      <SectionLabel title="Data ops — v1 imports + demo wipe">
        <DevDataPanel />
      </SectionLabel>

      <CardSection title="Worker health">
        <WorkerHealthBody status={workerHealth} details={healthDetails} />
      </CardSection>

      <SectionLabel title="Outlook">
        <OutlookDraftTest />
      </SectionLabel>

      <SectionLabel title="RFQ">
        <RfqExtractTest />
      </SectionLabel>

      <SectionLabel title="Reminders">
        <RemindersPanel />
      </SectionLabel>

      <SectionLabel title="Watchers">
        <WatcherAdmin />
      </SectionLabel>

      <SectionLabel title="Dev tools">
        <DevToolsCard />
      </SectionLabel>

      <CardSection title="Setup checklist">
        <SetupChecklistBody workerHealth={workerHealth} healthDetails={healthDetails} />
      </CardSection>
    </div>
  );
}
